/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { PillProps } from 'src/core/components/Pill';
import { PillContainer } from 'src/core/components/PillContainer';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { QnaMakerFilterItem, QnaMakerFiltersType } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerFiltersStore';

type QnaMakerFilterPillContainerProps = {
    filterItems: QnaMakerFilterItem[];
    onRemoveFilter: (filterType: QnaMakerFiltersType) => void;
};

const messages = defineMessages({
    sources: {
        id: 'qnaMaker.qnaMakerFilterPillsContainer.sources',
        defaultMessage: 'Sources'
    },
    metadata: {
        id: 'qnaMaker.qnaMakerFilterPillsContainer.metadata',
        defaultMessage: 'Metadata'
    },
    suggestionsType: {
        id: 'qnaMaker.qnaMakerFilterPillsContainer.suggestionsType',
        defaultMessage: 'Suggestion type'
    }
});

const filtersTheme = getScopedTheme('Filters');

/**
 * Renders a filter pill for the given filter type.
 */
export const QnaMakerFilterPillContainer = (props: QnaMakerFilterPillContainerProps) => {
    const { filterItems, onRemoveFilter } = props;
    const { localize } = useLocalization();

    const questionFilters = filterItems.filter(i => i.filterType === 'questions');
    const sourcesFilters = filterItems.filter(i => i.filterType === 'sources');
    const metadataFilters = filterItems.filter(i => i.filterType === 'metadata');
    const suggestionTypeFilters = filterItems.filter(i => i.filterType === 'suggestionsKind');
    const existingFilterPillsProps: PillProps[] = [];

    const getFilterNames = (filterItemsArray: QnaMakerFilterItem[]) => {
        let filterNames = '';
        filterItemsArray.forEach(item => {
            filterNames += item.name;
            if (item.id !== filterItemsArray[filterItemsArray.length - 1].id) {
                filterNames += '\n';
            }
        });

        return filterNames;
    };

    if (questionFilters.length) {
        questionFilters.forEach((qF, i) => {
            existingFilterPillsProps.push({
                index: i,
                name: qF.name,
                pillKey: qF.id,
                onRemovePill: () => onRemoveFilter('questions'),
                onRenderPillContent: () => <FilterPillContent filterName={qF.name} />
            });
        });
    }

    if (sourcesFilters.length) {
        if (sourcesFilters.length === 1) {
            existingFilterPillsProps.push({
                index: 1,
                name: sourcesFilters[0].name,
                pillKey: sourcesFilters[0].id,
                onRemovePill: () => onRemoveFilter('sources'),
                onRenderPillContent: () => <FilterPillContent filterName={sourcesFilters[0].name} filterType={localize(messages.sources)} />
            });
        } else {
            existingFilterPillsProps.push({
                index: 1,
                name: `${localize(messages.sources)} (${sourcesFilters.length})`,
                pillKey: sourcesFilters[0].id,
                onRemovePill: () => onRemoveFilter('sources'),
                onRenderPillContent: () => (
                    <FilterPillContent
                        title={getFilterNames(sourcesFilters)}
                        filterName={`${localize(messages.sources)} (${sourcesFilters.length})`}
                    />
                )
            });
        }
    }

    if (metadataFilters.length) {
        if (metadataFilters.length === 1) {
            existingFilterPillsProps.push({
                index: 2,
                name: metadataFilters[0].name,
                pillKey: metadataFilters[0].id,
                onRemovePill: () => onRemoveFilter('metadata'),
                onRenderPillContent: () => (
                    <FilterPillContent filterName={metadataFilters[0].name} filterType={localize(messages.metadata)} />
                )
            });
        } else {
            existingFilterPillsProps.push({
                index: 2,
                name: `${localize(messages.metadata)} (${metadataFilters.length})`,
                pillKey: metadataFilters[0].id,
                onRemovePill: () => onRemoveFilter('metadata'),
                onRenderPillContent: () => (
                    <FilterPillContent
                        title={getFilterNames(metadataFilters)}
                        filterName={`${localize(messages.metadata)} (${metadataFilters.length})`}
                    />
                )
            });
        }
    }

    if (suggestionTypeFilters.length) {
        existingFilterPillsProps.push({
            index: 1,
            name: suggestionTypeFilters[0].name,
            pillKey: suggestionTypeFilters[0].id,
            onRemovePill: () => onRemoveFilter('suggestionsKind'),
            onRenderPillContent: () => (
                <FilterPillContent filterName={suggestionTypeFilters[0].name} filterType={localize(messages.suggestionsType)} />
            )
        });
    }

    return <PillContainer allowAddPills={false} pills={existingFilterPillsProps} />;
};

const FilterPillContent = ({ filterType, filterName, title }: { filterType?: string; filterName: string; title?: string }) => (
    <Stack verticalAlign="center" horizontal tokens={{ childrenGap: 5 }}>
        {filterType && <LuisText>{filterType}:</LuisText>}
        <LuisText title={title} fontWeight={filtersTheme.boldFontWeight as Property.FontWeight}>
            {filterName}
        </LuisText>
    </Stack>
);
